/**
 * Deactivate and Reactivate on Users and Status (Trello 0LGomfTd).
 *
 * The API has a route and a right for each (`POST /users/{id}/deactivate`,
 * `users:deactivate`; `.../reactivate`, `users:reactivate`), and refuses three
 * things whoever asks: changing your own sign-in, taking the organization's
 * last active administrator's, and reactivating someone who is not
 * deactivated. Main's Users and Status had a Delete; the port keeps the person
 * (audit rows and signatures name them) and takes the sign-in instead.
 *
 * Deactivating staff removes every group membership and allowed override and
 * ends their care assignments; reactivating does NOT give those back. A
 * patient loses only the portal sign-in, and the chart stays. The
 * confirmations say so, because an administrator cannot undo the first by
 * pressing the second.
 */

import { deactivateUser, reactivateUser } from '@/api2/users';

const normalized = (value) => String(value == null ? '' : value).trim().toLowerCase();

/** A users-table row whose account is deactivated (`users.status`). */
export function isDeactivatedRow(row) {
  return normalized(row?.accountStatus ?? row?.activestatus ?? row?.account_status) === 'deactivated';
}

function isPatientRow(row) {
  return normalized(row?.account_type) === 'patient';
}

/**
 * The Actions cell for one row: Deactivate for anyone not deactivated, to a
 * holder of `users:deactivate`; Reactivate for a deactivated person, to a
 * holder of `users:reactivate`. Never on your own row — the API refuses it,
 * so a button would only ever fail.
 *
 * @param {object} row  a staffRows row (`unique_ID`, `accountStatus`)
 * @param {{canDeactivate: boolean, canReactivate: boolean, selfId: (string|number|null)}} rights
 */
export function accountActionCell(row, rights = {}) {
  const none = '<span class="linked-roles-none">&mdash;</span>';
  const id = String(row?.unique_ID ?? '').trim();

  if (id === '' || (rights.selfId != null && String(rights.selfId) === id)) return none;

  if (isDeactivatedRow(row)) {
    return rights.canReactivate
      ? '<a href="#" class="account-reactivate-link">Reactivate</a>'
      : none;
  }

  return rights.canDeactivate
    ? '<a href="#" class="account-deactivate-link">Deactivate</a>'
    : none;
}

/** What the administrator is asked before the request goes. */
export function accountLifecycleConfirmation(action, row, name) {
  if (action === 'reactivate') {
    return `Reactivate ${name}? They will be able to sign in again, but the groups and permissions `
      + 'they lost when they were deactivated are not restored: add them to the groups they need now.';
  }

  if (isPatientRow(row)) {
    return `Deactivate ${name}'s portal sign-in? Their chart, care team and contacts stay as they are.`;
  }

  return `Deactivate ${name}? They will no longer be able to sign in, and they will be removed from `
    + 'every group and every patient they are assigned to. Reactivating them later does not restore those.';
}

/** The sentence shown when the API refuses. */
const REFUSALS = {
  self_account_change_forbidden: 'You cannot change whether your own account may sign in.',
  last_administrator: 'This is the organization\'s last active administrator. '
    + 'Give another active person administrator rights first.',
  not_deactivated: 'This account is not deactivated, so there is nothing to reactivate.',
  forbidden: 'You do not have permission to do this.',
};

export function accountLifecycleRefusal(error) {
  return REFUSALS[error?.code] || error?.message || 'The account could not be changed. Please try again.';
}

/** Send the action for one person. */
export function runAccountLifecycleAction(action, userId) {
  return action === 'reactivate' ? reactivateUser(userId) : deactivateUser(userId);
}
